<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, ref, reactive, watch } from 'vue';

import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TeamsAPI from 'dashboard/api/teams';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const teamsList = computed(() => getters['teams/getTeams'].value);
const uiFlags = computed(() => getters['teams/getUIFlags'].value);
const { isAdmin } = useAdmin();

// Expand/collapse state
const expandedTeamId = ref(null);
const teamMembers = reactive({});
const loadingMembers = reactive({});
const memberCounts = reactive({});

const loading = ref({});

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    // Clean up expand state for deleted team
    if (expandedTeamId.value === id) expandedTeamId.value = null;
    delete teamMembers[id];
    delete memberCounts[id];
    delete loadingMembers[id];
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);

// Load member count for all teams on mount
const loadAllMemberCounts = async () => {
  const promises = teamsList.value.map(async team => {
    try {
      const { data } = await TeamsAPI.getAgents({ teamId: team.id });
      memberCounts[team.id] = (data || []).length;
    } catch {
      memberCounts[team.id] = 0;
    }
  });
  await Promise.all(promises);
};

// Watch for teams list to be ready and load counts
watch(
  () => teamsList.value.length,
  len => {
    if (len > 0) loadAllMemberCounts();
  },
  { immediate: true }
);

const toggleTeamMembers = async teamId => {
  // Collapse if already expanded
  if (expandedTeamId.value === teamId) {
    expandedTeamId.value = null;
    return;
  }

  expandedTeamId.value = teamId;

  // Always re-fetch to get fresh data (admin may have edited members)
  loadingMembers[teamId] = true;
  try {
    const { data } = await TeamsAPI.getAgents({ teamId });
    teamMembers[teamId] = data || [];
    memberCounts[teamId] = (data || []).length;
  } catch {
    teamMembers[teamId] = [];
  } finally {
    loadingMembers[teamId] = false;
  }
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('TEAMS_SETTINGS.HEADER')"
      :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
      :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
      feature-name="team_management"
      tooltip-identifier="teams_settings"
    >
      <template #actions>
        <router-link v-if="isAdmin" :to="{ name: 'settings_teams_new' }">
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
          />
        </router-link>
      </template>
    </BaseSettingsHeader>
    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.isFetching"
        :message="$t('TEAMS_SETTINGS.LOADING')"
      />
      <p
        v-else-if="!teamsList.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('TEAMS_SETTINGS.LIST.404') }}
      </p>

      <table v-else class="min-w-full divide-y divide-n-weak">
        <tbody class="divide-y divide-n-weak">
          <template v-for="team in teamsList" :key="team.id">
            <tr
              class="cursor-pointer hover:bg-n-alpha-1 transition-colors"
              @click="toggleTeamMembers(team.id)"
            >
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <div class="flex items-center gap-2">
                  <span class="block font-medium capitalize">{{
                    team.name
                  }}</span>
                  <span
                    v-if="memberCounts[team.id] != null"
                    class="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold bg-woot-75/15 text-woot-500 dark:bg-woot-800/30 dark:text-woot-400"
                  >
                    {{ memberCounts[team.id] }}
                  </span>
                  <fluent-icon
                    icon="chevron-right"
                    size="14"
                    class="text-n-slate-10 transition-transform duration-200"
                    :class="{
                      'rotate-90': expandedTeamId === team.id,
                    }"
                  />
                </div>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{ team.description }}
                </p>
              </td>

              <td class="py-4 flex justify-end gap-1">
                <router-link
                  :to="{
                    name: 'settings_teams_edit',
                    params: { teamId: team.id },
                  }"
                  @click.stop
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>

                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[team.id]"
                  @click.stop="openDelete(team)"
                />
              </td>
            </tr>

            <!-- Expanded members row -->
            <tr v-if="expandedTeamId === team.id">
              <td colspan="2" class="px-4 py-3 bg-n-alpha-1">
                <div
                  v-if="loadingMembers[team.id]"
                  class="text-sm text-n-slate-10 py-2"
                >
                  {{ $t('TEAMS_SETTINGS.LIST.LOADING_MEMBERS') }}
                </div>
                <div
                  v-else-if="
                    !teamMembers[team.id] || teamMembers[team.id].length === 0
                  "
                  class="text-sm text-n-slate-10 py-2"
                >
                  {{ $t('TEAMS_SETTINGS.LIST.NO_MEMBERS') }}
                </div>
                <div v-else class="flex flex-col gap-2">
                  <div
                    v-for="member in teamMembers[team.id]"
                    :key="member.id"
                    class="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
                  >
                    <Avatar
                      :src="member.thumbnail"
                      :name="member.name"
                      :status="member.availability_status"
                      :size="28"
                      rounded-full
                    />
                    <div class="flex flex-col">
                      <span
                        class="text-sm font-medium text-n-slate-12 leading-tight"
                      >
                        {{ member.name }}
                      </span>
                      <span class="text-xs text-n-slate-10 leading-tight">
                        {{ member.email || '---' }}
                      </span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>
